import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

interface UserProfile {
  id: number;
  userName: string;
  email: string;

  phoneNumber: string;
  azureToken: string;
}

interface UpdateUserProfileProps {
  updateUserName: (newUserName: string) => void;
}

const UpdateUserProfile: React.FC<UpdateUserProfileProps> = ({ updateUserName }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = location.state as { userProfile: UserProfile };

  const [userName, setUserName] = useState(userProfile.userName);
  const [phoneNumber, setPhoneNumber] = useState(userProfile.phoneNumber);
  const [errorMessage, setErrorMessage] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check for changes when userName or phoneNumber changes
  useEffect(() => {
    const hasChanges =
      userName !== userProfile.userName ||
      phoneNumber !== userProfile.phoneNumber;
    setIsFormChanged(hasChanges);
  }, [userName, phoneNumber, userProfile]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow letters and spaces
    if (/^[A-Za-z\s]*$/.test(value)) {
      setUserName(value);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow only numbers and '+'
    if (/^[\d+]*$/.test(value)) {
      setPhoneNumber(value);
    }

  };

  const handleUpdate = async () => {
    // Validate username
    if (!userName || userName.trim() === '') {
      toast.info('Please provide your full name');
      return;
    }

    setIsSubmitting(true);

    // Validate phone number
    if (!phoneNumber) {
      toast.info('Please provide your phone number');
      return;
    }

    // Validate phone number format
    const phoneStr = phoneNumber.toString();
    if (!/^\+?\d{10,18}$/.test(phoneStr)) { 
      toast.info('Please provide a valid phone number');
      return;
    }


    const token = localStorage.getItem("token");
    if (!token) {
      console.error('Token not found in localStorage');
      toast.error('Session expired. Please log in again.');
      navigate('/');
      return;
    }

    try {
      const response = await fetch('https://pamsapi.vofoxsolutions.com/api/User/UpdateUserProfile', {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          userName,
          phoneNumber: phoneStr,
          azureToken: userProfile.azureToken,
          isRegisteredUser: true,
        }),
      });

      if (!response.ok) throw new Error("Failed to update profile");
      const updatedProfile = await response.json();

      // Only update if we have a valid username
      if (updatedProfile.userName) {
        updateUserName(updatedProfile.userName);
        localStorage.setItem("userName", updatedProfile.userName);
        toast.success('Profile updated successfully');
        navigate("/home");
      } else {
        toast.error('Invalid profile data received');
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to update profile. Please try again.");
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const handleStorageChange = () => {
      const updatedUserName = localStorage.getItem('userName');
      if (updatedUserName) {
        setUserName(updatedUserName);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const fetchUserProfile = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setErrorMessage('Session expired. Please log in again.');
      navigate('/');
      return;
    }

    try {
      const response = await fetch('https://pamsapi.vofoxsolutions.com/api/User/GetUserProfile', {
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error('Failed to fetch profile');

      const data = await response.json();

      // Navigate to profile with user data
      navigate('/profile', {
        state: {
          userName: data.userName,
          email: data.email,
          phoneNumber: data.phoneNumber
        }
      });

      setDropdownOpen(false); // Close dropdown after navigation
    } catch (error) {
      console.error("Error fetching profile:", error);
      toast.error("Failed to load profile");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    sessionStorage.clear();
    navigate('/');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleUpdate();
    }
  };


  return (
    <div className="flex flex-col h-screen bg-gray-100 overflow-hidden">
      <header className="flex items-center justify-between px-6 py-3 bg-white shadow">
        <div>
          {/* <h1 className="text-lg font-semibold text-gray-700">Postal Address Mapping</h1> */}
        </div>
        <div className="flex items-center gap-6">
          <div className="relative m-3">
            <button
              onClick={toggleDropdown}
              className="relative border border-whitesmoke focus:outline-none flex rounded-full h-12 items-center px-2 gap-2"
            >
              <div className="w-8 h-8 overflow-hidden rounded-full">
                <img
                  className="object-cover w-full h-full"
                  src="https://cdn-icons-png.flaticon.com/128/18573/18573514.png"
                  alt="User Avatar"
                />
              </div>
              <h1>{userName || ''}</h1>
            </button>
            {dropdownOpen && (
              <>
                <div onClick={() => setDropdownOpen(false)} className="fixed inset-0 z-10 w-full h-full"></div>
                <div className="absolute right-0 z-20 w-48 mt-2 bg-white rounded-md shadow-xl">
                  <button
                    onClick={fetchUserProfile}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-600 hover:text-white"
                  >
                    Profile
                  </button>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-indigo-600 hover:text-white"
                  >
                    Log out
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      <div className="flex-grow p-6 bg-gray-100">
        <h1 className="text-2xl font-bold mb-6">Update User Profile</h1>
        <div className="w-full p-8 bg-white rounded-lg shadow-md">
          {errorMessage && <p className="text-red-500 mb-4">{errorMessage}</p>}

          <form className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="userName" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                id="userName"
                type="text"
                maxLength={15}
                className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={userName}
                onChange={handleNameChange}
                onKeyDown={handleKeyDown}
                placeholder="Enter letters only"
              />
            </div>

            <div>
              <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                id="phoneNumber"
                type="text"
                maxLength={18} 
                className="w-full px-4 py-2 border border-gray-200 rounded-md bg-gray-50 text-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={phoneNumber}
                onChange={handlePhoneChange}
                onKeyDown={handleKeyDown}
                placeholder="Enter numbers only"
              />

            </div>

            <div className="col-span-1 sm:col-span-2 flex items-center justify-end gap-4 mt-4">
              <button
                type="button"
                onClick={handleUpdate}
                disabled={!isFormChanged || isSubmitting}
                className={`py-2 px-4 text-white rounded-md shadow focus:outline-none focus:ring-2 focus:ring-blue-500
              ${isFormChanged && !isSubmitting
                    ? 'bg-blue-600 hover:bg-blue-700'
                    : 'bg-gray-400 cursor-not-allowed'}`}
              >
                {isSubmitting ? 'Updating...' : 'Update Profile'}
              </button>
              <button
                onClick={() => navigate('/home')}
                className="py-2 px-4 bg-gray-500 text-white rounded-md shadow hover:bg-gray-600 focus:outline-none"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <footer className="w-full mt-auto">
        <div className="px-6 py-6 bg-white shadow-shadow1 rounded-md">
          <p className="text-center text-jetgrey text-sm">
          GET ME THERE. © 2024
          </p>
        </div>
      </footer>
      <ToastContainer />
    </div>

  );
};

export default UpdateUserProfile;
